import React, { useCallback } from 'react';

import type { ControlSize } from '../../controls/shared/types';
import type { DisplayProps } from '../../utilities/shared/Display';
import {
  getVariantStyles,
  variantBackgroundColor,
  variantBodyColor,
  variantStrokeColor,
} from '../../common/variant';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import { selectors, transitions } from '../../controls/shared/styles';
import {
  controlBorderRadius,
  controlPaddingX,
  controlPaddingY,
  controlVars,
} from '../../controls/shared/vars';
import { darkThemeSelector, shadows, styled } from '../../stitches.config';
import { copyTextToClipboard } from '../../utilities/clipboard';
import { defer } from '../../utilities/defer';
import { displayCSS } from '../../utilities/shared/Display';
import { useTimedToggle } from '../../utilities/useTimedToggle';

const CopyBoxValue = styled('span', {
  position: 'relative',
  zIndex: '2',
  display: 'flex',

  // Make sure the value inherits the font properties from the text-component further up the DOM.
  fontFamily: 'inherit',
  fontSize: 'inherit',
  fontWeight: 'inherit',
  lineHeight: 'inherit',

  variants: {
    isClicked: {
      true: {
        color: variantBodyColor,
      },
      false: {},
    },
    wrap: {
      wrap: {
        wordBreak: 'break-word',
        whiteSpace: 'pre-line',
      },
      nowrap: {
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    },
  },
});
CopyBoxValue.displayName = 'CopyBoxValue';

const CopyBoxBoundary = styled('div', {
  position: 'absolute',
  zIndex: 1,
  background: variantBackgroundColor,
  strokeAll: variantStrokeColor,
  borderRadius: controlBorderRadius,
  opacity: '$$boundaryOpacity',
  transition: transitions.control,

  variants: {
    relation: {
      stacked: {
        top: `calc(${controlPaddingY} * -1)`,
        right: `calc(${controlPaddingX} * -1)`,
        bottom: `calc(${controlPaddingY} * -1)`,
        left: `calc(${controlPaddingX} * -1)`,
      },
      standalone: {},
    },
  },
});
CopyBoxBoundary.displayName = 'CopyBoxBoundary';

const CopyBoxContainer = styled('div', {
  position: 'relative',
  maxWidth: '100%',
  cursor: 'pointer',
  ...getVariantStyles('neutral'),

  [selectors.focus]: {
    outline: 'none',
  },

  variants: {
    isClicked: {
      true: {
        [selectors.all]: {
          ...getVariantStyles('brand'),
        },
      },
      false: {},
    },
    isInline: {
      true: {},
      false: {},
    },
    relation: {
      stacked: {
        $$boundaryOpacity: 0,

        [selectors.hover]: {
          $$boundaryOpacity: 0.8,
        },

        [selectors.focus]: {
          $$boundaryOpacity: 0.8,

          [`${CopyBoxBoundary}`]: {
            boxShadow: shadows.focusRingLight,

            [darkThemeSelector]: {
              boxShadow: shadows.focusRingDark,
            },
          },
        },
      },
      standalone: {
        padding: `${controlPaddingY} ${controlPaddingX}`,
        background: variantBackgroundColor,
        strokeAll: variantStrokeColor,
        borderRadius: controlBorderRadius,
        $$boundaryOpacity: 1,
      },
    },
    size: {
      'x-small': { ...controlVars.size['x-small'] },
      small: { ...controlVars.size.small },
      medium: { ...controlVars.size.medium },
      large: { ...controlVars.size.large },
      'x-large': { ...controlVars.size['x-large'] },
    },
  },

  compoundVariants: [
    {
      isClicked: true,
      relation: 'stacked',
      css: {
        $$boundaryOpacity: 0.8,
      },
    },
  ],
});
CopyBoxContainer.displayName = 'CopyBoxContainer';

export type CopyBoxRelation = 'stacked' | 'standalone';
export type CopyBoxWrap = 'nowrap' | 'wrap';

export type CopyBoxProps = {
  /**
   * Description of the action taken when clicking the component. **Required** for accessibility support.
   */
  'aria-label': React.ReactNode;
  /**
   * Displayed as the contents of the component.
   */
  children: React.ReactNode;
  /**
   * Determines how the component is positioned with its surroundings.
   */
  relation?: CopyBoxRelation;
  /**
   * Set which sizing you want.
   */
  size?: ControlSize;
  /**
   * Value to copy to the clipboard when the CopyBox is clicked, defaults to the value of `children`.
   */
  value?: React.ReactNode;
  /**
   * Set whether the contents should wrap or not.
   */
  wrap?: CopyBoxWrap;
  /**
   * Customizes the tooltip label.
   */
  tooltipLabel?: React.ReactNode;
  /**
   * Called after the value has been copied to the clipboard.
   */
  onCopy?: () => void;
} & DisplayProps;

export function CopyBox({
  'aria-label': ariaLabel = 'Copy to clipboard',
  tooltipLabel = ariaLabel,
  children,
  display = 'flex',
  relation = 'stacked',
  size = 'small',
  value,
  wrap = 'wrap',
  onCopy,
  ...props
}: CopyBoxProps) {
  const { value: isClicked, hide, show } = useTimedToggle();
  const valueFallback = value ? `${value}` : `${children}`;
  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      await copyTextToClipboard(valueFallback);
      onCopy?.();
      hide();
      await defer(50);
      show();
    },
    [valueFallback, hide, show, onCopy],
  );

  const css = displayCSS(display);

  return (
    <Tooltip asChild contents={tooltipLabel}>
      <CopyBoxContainer
        {...props}
        role="button"
        tabIndex={0}
        aria-label={`${ariaLabel}`}
        onClick={handleClick}
        isClicked={isClicked}
        relation={relation}
        size={size}
        css={css}
      >
        <CopyBoxValue isClicked={isClicked} wrap={wrap}>
          {children}
        </CopyBoxValue>
        <CopyBoxBoundary relation={relation} />
      </CopyBoxContainer>
    </Tooltip>
  );
}
